import { useCallback, useEffect, useState } from "react";
import api, { qs, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { monthOptions, monthLabel } from "@/lib/dpc";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { OverviewHeader } from "@/components/overview/OverviewHeader";
import { FunnelTiles } from "@/components/overview/FunnelTiles";
import { MomentumChart } from "@/components/overview/MomentumChart";
import { DealerTable } from "@/components/overview/DealerTable";
import { MatrixTable, PeerRank } from "@/components/overview/MatrixTable";
import { ModelTable, SourceTable } from "@/components/overview/ModelSourceTables";
import { WeekStrip, CallOuts } from "@/components/overview/WeekStrip";
import { ScopePicker } from "@/components/overview/ScopePicker";
import { PeriodControl, AsOfBanner, WeekSummary } from "@/components/overview/PeriodControl";
import { TdModelDemand } from "@/components/overview/TdModelDemand";

function Filters({ model, setModel, source, setSource, meta }) {
  return (
    <>
      <Select value={model || "ALL"} onValueChange={(v) => setModel(v === "ALL" ? "" : v)}>
        <SelectTrigger className="h-8 w-[130px] text-xs" data-testid="ov-model-filter"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="ALL">All models</SelectItem>{(meta?.models || []).filter((x) => x.active).map((x) => <SelectItem key={x.name} value={x.name}>{x.name}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={source || "ALL"} onValueChange={(v) => setSource(v === "ALL" ? "" : v)}>
        <SelectTrigger className="h-8 w-[150px] text-xs" data-testid="ov-source-filter"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="ALL">All sources</SelectItem>{(meta?.sources || []).filter((x) => x.active).map((x) => <SelectItem key={x.name} value={x.name}>{x.name}</SelectItem>)}</SelectContent>
      </Select>
    </>
  );
}

export default function DpcOverview() {
  const { user } = useApp();
  const [meta, setMeta] = useState(null);
  const [month, setMonth] = useState(monthOptions(1, 0)[0]);
  const [period, setPeriod] = useState("month");
  const [asOf, setAsOf] = useState("");
  const [region, setRegion] = useState("");
  const [dealer, setDealer] = useState("");
  const [model, setModel] = useState("");
  const [source, setSource] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/dpc/meta").then(({ data }) => setMeta(data)).catch(() => {}); }, []);
  const params = { month, period, as_of: asOf, region, dealer, model, source };
  const load = useCallback(() => {
    setLoading(true);
    api.get(`/dpc/overview?${qs(params)}`).then(({ data }) => setData(data)).catch((e) => toast.error(apiError(e), { id: "ov-error" })).finally(() => setLoading(false));
  }, [month, period, asOf, region, dealer, model, source]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(load, [load]);

  const changeMonth = (m) => { setMonth(m); setPeriod("month"); setAsOf(""); };  // every month opens in Month view
  const h = data?.header;
  const per = h?.period;
  const isWeek = per?.kind === "week";
  const dealerScope = h?.scope_kind === "dealer";
  const filtersNote = [model && ` · model ${model}`, source && ` · source ${source}`].filter(Boolean).join("");
  const title = `${h?.breadcrumb?.join(" › ") || (user?.role === "manager" ? "My dealership" : user?.role === "zonal" ? "My zone" : "Group")}`;
  return (
    <div className="space-y-4" data-testid="dpc-overview">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-extrabold tracking-tight">Group Overview <span className="text-muted-foreground font-medium text-base" data-testid="ov-title-scope">· {title}{isWeek ? ` · ${per.week}` : ""}</span></h1>
          <p className="text-sm text-muted-foreground">What the team committed to, where we stand and which way every number is moving — {isWeek ? `${per.week} of ${monthLabel(month)}` : "this month"}</p>
        </div>
        <div className="flex flex-wrap items-start gap-2" data-testid="ov-controls">
          <ScopePicker picker={data?.picker} region={region} dealer={dealer} breadcrumb={h?.breadcrumb} onChange={({ region: r, dealer: d }) => { setRegion(r || ""); setDealer(d || ""); }} />
          <Select value={month} onValueChange={changeMonth}>
            <SelectTrigger className="h-8 w-[120px] text-xs" data-testid="ov-month-select"><SelectValue /></SelectTrigger>
            <SelectContent>{monthOptions(13, 0).map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}</SelectContent>
          </Select>
          <PeriodControl period={period} setPeriod={setPeriod} weeks={h?.weeks} month={month} asOf={asOf} setAsOf={setAsOf} />
          <Filters model={model} setModel={setModel} source={source} setSource={setSource} meta={meta} />
        </div>
      </div>

      <AsOfBanner asOf={asOf} onClear={() => setAsOf("")} />

      {!data && <div className="text-sm text-muted-foreground py-20 text-center" data-testid="ov-loading">Loading group overview…</div>}
      {data && (
        <div className={`space-y-4 transition-opacity ${loading ? "opacity-60" : ""}`} data-period={per?.kind} data-scope={h.scope_kind}>
          <OverviewHeader h={h} />
          {isWeek && <WeekSummary weeks={data.weeks} selected={per.week} onPick={setPeriod} />}
          <div className="grid gap-4 2xl:grid-cols-5 [&>*]:min-w-0">
            <div className="2xl:col-span-3 min-w-0"><FunnelTiles funnel={data.funnel} conversions={data.conversions} period={per} /></div>
            <div className="2xl:col-span-2 min-w-0"><MomentumChart chart={data.chart} asOf={h.as_of} period={per} /></div>
          </div>
          {dealerScope ? (
            <>
              <MatrixTable matrix={data.matrix} period={per} dealerName={data.picker?.dealers?.find((d) => d.code === h.dealer)?.name || ""} />
              <PeerRank peers={data.peers} isWeek={isWeek} />
            </>
          ) : (
            <DealerTable rows={data.dealers} regions={data.regions} total={data.total} regionLabels={data.region_labels} isManager={h.is_manager} filtersNote={filtersNote} period={per} />
          )}
          <div className="grid gap-4 xl:grid-cols-2 [&>*]:min-w-0">
            <ModelTable rows={data.models} active={model} onPick={setModel} period={per} />
            <SourceTable rows={data.sources} active={source} onPick={setSource} period={per} />
          </div>
          <TdModelDemand d={data.td_models} period={per} />
          {isWeek ? (
            <CallOuts items={data.callouts} period={per} />
          ) : (
            <div className="grid gap-4 xl:grid-cols-5 [&>*]:min-w-0">
              <div className="xl:col-span-3"><WeekStrip weeks={data.weeks} /></div>
              <div className="xl:col-span-2"><CallOuts items={data.callouts} period={per} /></div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
